import {
  getApp,
  getApps,
  initializeApp,
  registerVersion,
} from 'firebase/app';
import React, {
  createContext,
  useContext,
  useMemo,
  version,
} from 'react';
import PropTypes from 'prop-types';

const propTypes = {
  children: PropTypes.node,
  config: PropTypes.object.isRequired,
};
const defaultProps = {
  children: undefined,
};

const FirebaseAppContext = createContext(null);

// App version for Firebase's registerVersion telemetry. Injected at build time
// by webpack DefinePlugin (`process.env.PACKAGE_VERSION` = the ui package.json
// version); undefined under jest, where it falls back to 'unknown' below.
const appVersion = process.env.PACKAGE_VERSION;


export function FirebaseAppProvider({ children, config }) {

  const initializedApp = useMemo(() => {
    registerVersion('react', version || 'unknown');
    registerVersion('app', appVersion || 'unknown');

    // Reuse the already-initialized default app when present. The
    // framework-agnostic firebaseAuth.js initializes it at import time for
    // non-React consumers, so re-initializing here would throw
    // "Firebase App named '[DEFAULT]' already exists".
    return getApps().length ? getApp() : initializeApp(config);
  }, [config]);

  return (
    <FirebaseAppContext.Provider value={initializedApp}>
      {children}
    </FirebaseAppContext.Provider>
  );
}

FirebaseAppProvider.propTypes = propTypes;
FirebaseAppProvider.defaultProps = defaultProps;

export function useFirebaseApp() {
  const app = useContext(FirebaseAppContext);

  if (!app) {
    throw new Error(
      'You cannot use the useFirebaseApp hook outside of a <FirebaseAppProvider />',
    );
  }

  return app;
}